import _ from 'lodash';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Heading, Select, Tabs } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import OverallScoreMetric from 'packages/kakapo-ui-application/pages/dashboard/metrics/engagement/OverallScoreMetric';
import ParticipationRatesMetric from 'packages/kakapo-ui-application/pages/dashboard/metrics/engagement/ParticipationRatesMetric';
import EngagementMetric from 'packages/kakapo-ui-application/pages/dashboard/metrics/engagement/EngagementMetric';
import AllScoresTable from 'packages/kakapo-ui-application/pages/dashboard/metrics/engagement/AllScoresTable';
import DashboardEngagementTopics from 'packages/kakapo-ui-application/pages/dashboard/DashboardEngagementTopics';
import DashboardEngagementQuestions from 'packages/kakapo-ui-application/pages/dashboard/DashboardEngagementQuestions';
import EngagementNpsAnalytics from './EngagementNpsAnalytics';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import EngagementRequestFilter from './EngagementRequestFilter';
import { useRouteMatch } from 'react-router';
import { useHistory } from '@treams/treams-routes';

enum EngagementTab {
	Overview,
	Nps,
	Topics,
	Feedback,
	Matrix,
}

const Column = styled(Box)`
	flex-shrink: 0;
`;

function DashboardEngagementMetrics(props: {
	data:
		| graphql.WorkspaceEngagementMetricsQuery
		| graphql.EngagementOfficerMetricsQuery
		| graphql.GroupEngagementMetricsQuery;
	filters: graphql.AnalyticsFilters;
	tab: EngagementTab;
}) {
	const { t } = useTranslation();

	const data =
		'workspaceAnalytics' in props.data
			? props.data.workspaceAnalytics
			: 'groupAnalytics' in props.data
			? props.data.groupAnalytics
			: props.data.engagementAnalytics;

	const participation =
		data.engagementGroupsParticipation as graphql.AnalyticsEngagementGroupParticipation[];

	if (props.tab === EngagementTab.Overview) {
		return (
			<>
				<Flex flexWrap="wrap" mt={3}>
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<OverallScoreMetric
								data={data.engagementScoreOverTime as graphql.AnalyticsEngagementScore}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<ParticipationRatesMetric
								data={
									data.engagementParticipationOverTime as graphql.AnalyticsEngagementParticipation
								}
							/>
						</Box>
					</Column>
				</Flex>

				<Heading mt={4} mb={3}>
					{t('dashboard.engagement.thrivingTopics.title')}
				</Heading>
				<Flex flexWrap="wrap" mt={3}>
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementThrivingSubcategories[0] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementThrivingSubcategories[1] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
				</Flex>

				<Heading mt={4} mb={3}>
					{t('dashboard.engagement.decliningTopics.title')}
				</Heading>
				<Flex flexWrap="wrap">
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementDecliningSubcategories[0] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementDecliningSubcategories[1] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
				</Flex>
			</>
		);
	}

	if (props.tab === EngagementTab.Topics) {
		return (
			<Box mt={3}>
				<DashboardEngagementTopics filters={props.filters} headless />
			</Box>
		);
	}

	if (props.tab === EngagementTab.Nps) {
		return (
			<Box mt={3}>
				<EngagementNpsAnalytics filters={props.filters} />
			</Box>
		);
	}

	if (props.tab === EngagementTab.Feedback) {
		return (
			<Box mt={3}>
				<DashboardEngagementQuestions filters={props.filters} headless />
			</Box>
		);
	}

	if (props.tab === EngagementTab.Matrix) {
		return data.engagementGroupsSubcategoriesScore.length > 0 ? (
			<Box mt={5}>
				<AllScoresTable
					scores={
						data.engagementGroupsSubcategoriesScore as graphql.AnalyticsEngagementGroupSubcategoryScore[]
					}
					participation={[
						...participation,
						{
							participation: data.engagementParticipationOverTime.average ?? undefined,
							delta: data.engagementParticipationOverTime.delta ?? undefined,
						},
					]}
				/>
			</Box>
		) : null;
	}

	return null;
}

function WorkspaceEngagement(props: { filters: graphql.AnalyticsFilters; tab: EngagementTab }) {
	const query = graphql.useWorkspaceEngagementMetricsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} tab={props.tab} filters={props.filters} />;
}

function GroupEngagement(props: { filters: graphql.AnalyticsFilters; tab: EngagementTab }) {
	const query = graphql.useGroupEngagementMetricsQuery({
		variables: {
			filters: props.filters,
		},
		skip: !props.filters.groupIDs || props.filters.groupIDs.length === 0,
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} tab={props.tab} filters={props.filters} />;
}

function EngagementOfficerEngagement(props: {
	filters: graphql.AnalyticsFilters;
	tab: EngagementTab;
}) {
	const query = graphql.useEngagementOfficerMetricsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} tab={props.tab} filters={props.filters} />;
}

function EngagementAnalyticsContents(props: {
	filters: graphql.AnalyticsFilters;
	tab: EngagementTab;
}) {
	const userRoles = useViewerRoles();

	if (userRoles.isEngagementOfficer) {
		return <EngagementOfficerEngagement filters={props.filters} tab={props.tab} />;
	}

	if (userRoles.isWorkspaceSuperuser || userRoles.isWorkspaceSupervisor) {
		return <WorkspaceEngagement filters={props.filters} tab={props.tab} />;
	}

	return <GroupEngagement filters={props.filters} tab={props.tab} />;
}

export default function EngagementAnalytics(props: {
	filters: graphql.AnalyticsFilters;
	eNPS?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { location, push } = useHistory();
	const { params } = useRouteMatch<{ metricGroupID: string; metricID: string; tabID?: string }>();
	const tabKey = _.upperFirst(_.camelCase(params.tabID));

	const tab = Object.values(EngagementTab).includes(tabKey)
		? EngagementTab[tabKey]
		: props.eNPS
		? EngagementTab.Nps
		: EngagementTab.Overview;

	const setTab = (newTab: EngagementTab) => {
		const newTabKey = _.kebabCase(EngagementTab[newTab]);
		const search = location.search;

		push(`/analytics/v2/metric/${params.metricGroupID}/${params.metricID}/${newTabKey}${search}`);
	};

	const [templateID, setTemplateID] = useState<string | undefined>();
	const [requestID, setRequestID] = useState<string | undefined>();

	const [fetch, query] = graphql.useEngagementSettingsLazyQuery();

	useEffect(() => {
		setRequestID(undefined);
	}, [templateID]);

	return (
		<>
			<Flex alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					{t('analytics.metric.detailsTitle')}
				</Heading>
				<Box mr={2}>
					<Select
						selectedOption={
							templateID
								? undefined
								: {
										title: t('analytics.metric.engagement.filters.template.all'),
										value: undefined,
								  }
						}
						selectedValue={templateID}
						options={
							query.data
								? [
										{
											title: t('analytics.metric.engagement.filters.template.all'),
											value: undefined,
										},
										undefined,
										..._.sortBy(
											query.data?.engagementTemplates.edges.map(({ node }) => ({
												title: localize(node.title).text,
												value: node.id,
											})),
											'title',
										),
								  ]
								: undefined
						}
						fetch={fetch}
						onChange={(value) => setTemplateID(value!)}
					/>
				</Box>
				<EngagementRequestFilter
					templateID={templateID}
					value={requestID}
					onSelect={setRequestID}
				/>
			</Flex>
			<Tabs>
				<Flex ml={-3}>
					<Tabs.Item
						title={t('dashboard.engagement.overview.title')}
						onClick={() => setTab(EngagementTab.Overview)}
						selected={tab === EngagementTab.Overview}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.nps.title')}
						onClick={() => setTab(EngagementTab.Nps)}
						selected={tab === EngagementTab.Nps}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.topics.title')}
						onClick={() => setTab(EngagementTab.Topics)}
						selected={tab === EngagementTab.Topics}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.questions.title')}
						onClick={() => setTab(EngagementTab.Feedback)}
						selected={tab === EngagementTab.Feedback}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.matrix.title')}
						onClick={() => setTab(EngagementTab.Matrix)}
						selected={tab === EngagementTab.Matrix}
					/>
				</Flex>
			</Tabs>
			<EngagementAnalyticsContents
				filters={{
					...props.filters,
					// Only load templateIDs when a templateID is selected and no requestIDs are selected.
					// Once a requestID is selected, the templateID should thus be ingored.
					engagementTemplateIDs: templateID && requestID === undefined ? [templateID] : undefined,
					engagementRequestID: requestID,
				}}
				tab={tab}
			/>
		</>
	);
}
